import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/types";

interface HeroCarouselProps {
  items: Film[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[420px] w-full md:h-[560px]">
        {items.map((film, index) => (
          <div
            key={film.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={film.backdrop}
              alt={`${film.title}影视主视觉`}
              title={`${film.title} - 久久爱热门影视推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/20"></div>
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full items-center px-4 md:px-6">
          <div className="w-full max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
              <i className="ri-fire-fill"></i>
              本站热播推荐
            </span>
            <h1 className="mt-4 text-3xl font-extrabold leading-tight text-background-50 md:text-5xl font-heading">
              {items[active].title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill"></i>
                {items[active].rating.toFixed(1)} 分
              </span>
              <span>{items[active].year}</span>
              <span>{items[active].region}</span>
              <span>{items[active].genres.join(" / ")}</span>
            </div>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
              {items[active].summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[active].id}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-compass-3-line text-lg"></i>
                浏览全部
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-4 z-10 flex items-center gap-2 md:right-6">
          {items.map((film, index) => (
            <button
              key={film.id}
              type="button"
              aria-label={`切换到 ${film.title}`}
              onClick={() => setActive(index)}
              className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
                index === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}